"use client";

import { clsx } from "clsx";
import * as Slider from "@radix-ui/react-slider";

export type RangeSliderProps = {
  /**
   * null renders the thumb parked at the step-aligned midpoint with no fill
   * and a muted border — "untouched" rather than a real chosen value. The
   * first onValueChange call always carries a real number; there is no way
   * to drag back to null.
   */
  value: number | null;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  onValueChange: (value: number) => void;
  fillColorVar?: string;
  "aria-label"?: string;
  /**
   * Renders a decorative 49-mark tick ruler beneath the track, faded in sync
   * with it. Purely visual — marks are evenly spaced, not tied to any value
   * (including CURRENCY_CONFIG.ticks — see quizConstants.ts). Opt-in because
   * the ruler is budget-slider-specific; the track's end-fade below is not
   * gated by this prop and applies to every RangeSlider.
   */
  showRuler?: boolean;
  /**
   * aria-valuetext while value is null. Opt-in (like showRuler) so this
   * generic component doesn't hardcode budget-specific copy — the caller
   * supplies wording appropriate to what the slider represents.
   */
  unsetValueText?: string;
  /**
   * Renders the thumb border in the danger color and sets aria-invalid.
   * Opt-in, like showRuler/unsetValueText — callers decide when "unset"
   * should also read as "required and not yet answered".
   */
  invalid?: boolean;
};

// Track/ruler ends dissolve to transparent over this fraction at each side —
// signals "soft edge" ($5,000+) rather than a hard-stopped pill. Final value
// from design review (8% / 14% were considered and rejected). Deliberately
// unconditional: RangeSlider has one consumer today and it always wants this.
// If a future caller needs a hard-edged rail, gate this behind a prop then —
// don't remove it speculatively now.
const FADE_MASK =
  "linear-gradient(90deg, transparent 0, #000 22%, #000 78%, transparent 100%)";

const RULER_MARKS = Array.from({ length: 49 }, (_, i) => i);

export function RangeSlider({
  value,
  min,
  max,
  step,
  disabled,
  onValueChange,
  fillColorVar = "--bsa-primary",
  "aria-label": ariaLabel,
  showRuler = false,
  unsetValueText,
  invalid = false,
}: RangeSliderProps) {
  const isUnset = value === null;
  // Step-aligned midpoint — the Radix thumb position while unset.
  const midpoint = min + Math.round((max - min) / 2 / (step ?? 1)) * (step ?? 1);
  const safe = isUnset ? midpoint : Math.min(max, Math.max(min, value));
  return (
    <div className="w-full">
      <Slider.Root
        className="relative flex w-full touch-none select-none items-center"
        value={[safe]}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onValueChange={([v]) => onValueChange(v!)}
        // While unset, the controlled value is already the midpoint — a tap
        // on the thumb with no drag snaps to the same value, so Radix's own
        // updateValues() (dist/index.mjs) sees no change and never fires
        // onValueChange. That leaves the most natural first touch gesture
        // (tap, not drag) producing zero feedback. Commit the midpoint
        // ourselves on pointerdown to rescue that no-op case; Radix composes
        // this handler before its own at both Root and Thumb, so a pointerdown
        // that lands elsewhere on the track still ends up applying Radix's
        // own (different) value right after ours, in the same batch.
        onPointerDown={isUnset ? () => onValueChange(safe) : undefined}
      >
        <Slider.Track
          className="relative h-[6px] w-full grow overflow-hidden rounded-full"
          style={{
            backgroundColor: "var(--bsa-bush-100)",
            maskImage: FADE_MASK,
            WebkitMaskImage: FADE_MASK,
          }}
        >
          {isUnset ? null : (
            <Slider.Range
              data-testid="range-slider-fill"
              className="absolute h-full"
              style={{ backgroundColor: `var(${fillColorVar})` }}
            />
          )}
        </Slider.Track>
        <Slider.Thumb
          aria-label={ariaLabel}
          aria-valuetext={isUnset ? unsetValueText : undefined}
          aria-invalid={invalid || undefined}
          className={clsx(
            "block h-[20px] w-[20px] rounded-full bg-surface",
            "border-2 transition-colors shadow-sm",
            "focus-ring",
            "cursor-grab active:cursor-grabbing data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
          style={{
            borderColor:
              isUnset && invalid
                ? "var(--bsa-danger)"
                : isUnset
                  ? "var(--bsa-bush-400)"
                  : `var(${fillColorVar})`,
          }}
        />
      </Slider.Root>
      {showRuler ? (
        <div
          aria-hidden="true"
          data-testid="range-slider-ruler"
          className="relative mt-[9px] h-[9px] pointer-events-none"
          style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
        >
          {RULER_MARKS.map((i) => {
            const isMajor = i % 6 === 0;
            const isOdd = i % 2 !== 0;
            return (
              <span
                key={i}
                className={clsx(
                  "absolute top-0 w-px -translate-x-1/2",
                  isOdd && "max-[360px]:hidden",
                )}
                style={{
                  left: `${(i / (RULER_MARKS.length - 1)) * 100}%`,
                  height: isMajor ? 9 : 5,
                  backgroundColor: `var(${isMajor ? "--bsa-bush-400" : "--bsa-bush-300"})`,
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
